import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import '../../../../../assets/styles/editor/styles.css';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Editor, Extension } from '@tiptap/core';
import Placeholder from '@tiptap/extension-placeholder';
import Underline from '@tiptap/extension-underline';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import dayjs, { Dayjs } from 'dayjs';
import { useState } from 'react';

import DescriptionTextField from './DescriptionField';
import FieldHeader from './FieldHeader';
import TitleField from './TitleField';

import { ActiveMarks } from '../../../../../interfaces/ActiveMarks';
import { CustomFieldProps } from '../../../../../interfaces/CustomFieldProps';
import { DynamicField } from '../../../../../interfaces/DynamicField';
import { TextStyleExtended } from '../../../../common/editor/extensions/FontSize';
import { fieldTypes } from '../../../settings/finalReport/templateDialogs/common/fieldTypes';

interface CustomFieldsProps {
  fields: DynamicField[];
  setValues: React.Dispatch<React.SetStateAction<DynamicField[]>>;
  valueRef: React.MutableRefObject<DynamicField[]>;
  setEditor: (editor: Editor | null) => void;
  setMarks: (newMarks: ActiveMarks) => void;
  checkChanges: () => void;
}

interface SmallTextFieldProps {
  info: DynamicField;
  rootIndex: number;
  siblingIndex?: number;
  isSibling?: boolean;
}

const CustomFields = ({
  fields,
  valueRef,
  setEditor,
  setMarks,
  checkChanges,
}: CustomFieldsProps) => {
  const dataFields = fields;

  const resetMarks = () => {
    setMarks({
      bold: false,
      italic: false,
      underline: false,
      bulletList: false,
      numberedList: false,
      heading: false,
      textStyle: false,
    });
  };

  const inputFieldStyle = {
    borderRadius: '6px',
    margin: '4px 0px',
    border: '1px solid transparent',
    '&:focus-within': {
      border: '1px solid black',
    },
    '& .MuiFilledInput-root:after': {
      border: 'none',
    },
    '& .MuiFilledInput-root:before': {
      borderBottom: 'none',
    },
    '& .MuiFilledInput-root': {
      backgroundColor: 'rgba(0, 0, 0, 0.0275)',
    },
    '& .MuiFilledInput-root:hover:not(.Mui-disabled,.Mui-error):before': {
      borderBottom: 'none',
    },
  };

  const multiLineInputFieldStyle = {
    minHeight: '280px',
    maxHeight: '480px',
    borderRadius: '6px',
    margin: '4px 0px',
    border: '1px solid transparent',
    backgroundColor: 'rgba(0, 0, 0, 0.0275)',
    display: 'flex',
    '&:focus-within': {
      border: '1px solid black',
    },
    '& .MuiFilledInput-root:after': {
      border: 'none',
    },
    '& .MuiFilledInput-root:before': {
      borderBottom: 'none',
    },
    '& .MuiFilledInput-root': {
      backgroundColor: 'rgba(0, 0, 0, 0.0275)',
    },
    '& .MuiFilledInput-root:hover:not(.Mui-disabled,.Mui-error):before': {
      borderBottom: 'none',
    },
  };

  const inputStyles: React.CSSProperties = {
    height: '3.5rem',
    overflow: 'hidden',
    paddingLeft: '0.5rem',
    paddingRight: '0.5rem',
    border: '',
    borderRadius: '6px',
  };

  const enableFormattingShortcuts = Extension.create({
    addKeyboardShortcuts() {
      return {
        'Mod-b': () => {
          return false;
        },
        'Mod-i': () => {
          return false;
        },
        'Mod-u': () => {
          return false;
        },
      };
    },
  });

  const handleSiblingValueChange = (
    text: string,
    siblingIndex: number,
    rootIndex: number,
  ) => {
    const newFields = [...fields];
    if (newFields[rootIndex].siblings) {
      newFields[rootIndex].siblings![siblingIndex].value = text;
      valueRef.current = newFields;
      checkChanges();
    }
  };

  const handleSiblingDateChange = (
    value: Dayjs | null,
    rootIndex: number | undefined,
    siblingIndex: number,
  ) => {
    const newDate = dayjs(value).format('YYYY-MM-DD');
    if (rootIndex !== undefined) {
      const newFields = [...fields];
      if (newFields[rootIndex].siblings) {
        newFields[rootIndex].siblings![siblingIndex].value = newDate;
        valueRef.current = newFields;
        checkChanges();
      }
    }
  };

  const handleValueChange = (text: string, index: number) => {
    const newFields = [...fields];
    newFields[index].value = text;
    valueRef.current = newFields;
    checkChanges();
  };

  const handleDateChange = (value: Dayjs | null, index: number | undefined) => {
    const newDate = dayjs(value).format('YYYY-MM-DD');
    if (index !== undefined) {
      const newFields = [...fields];
      newFields[index].value = newDate;
      valueRef.current = newFields;
      checkChanges();
    }
  };

  const SmallTextField = ({
    info,
    rootIndex,
    siblingIndex,
    isSibling,
  }: SmallTextFieldProps) => {
    return (
      <Box
        flexGrow={1}
        marginY={1}
        maxWidth='460px'
        onFocus={() => {
          setEditor(null);
          resetMarks();
        }}
      >
        <FieldHeader title={info.fieldTitle} isMandatory={info.isMandatory} />
        <TextField
          variant='filled'
          hiddenLabel
          fullWidth
          placeholder={info.placeholder}
          defaultValue={info.value}
          sx={inputFieldStyle}
          InputProps={{
            disableUnderline: true,
            sx: {
              ...inputStyles,
              fontSize: 16,
            },
          }}
          onChange={(event) => {
            if (isSibling && siblingIndex !== undefined && siblingIndex >= 0) {
              handleSiblingValueChange(
                event.target.value,
                siblingIndex,
                rootIndex,
              );
              return;
            }
            handleValueChange(event.target.value, rootIndex);
          }}
        />
        {info.errorMessage && (
          <Typography color='error' marginTop={1}>
            {info.errorMessage}
          </Typography>
        )}
      </Box>
    );
  };

  const DateField = ({
    field,
    index,
    isSibling,
    siblingIndex,
  }: CustomFieldProps) => (
    <Box
      marginBottom='24px'
      maxWidth='460px'
      onFocus={() => {
        setEditor(null);
        resetMarks();
      }}
    >
      <FieldHeader title={field.fieldTitle} isMandatory={field.isMandatory} />
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DatePicker
          label='Select date'
          onChange={(value: Dayjs | null) => {
            if (isSibling && siblingIndex !== undefined && siblingIndex >= 0) {
              handleSiblingDateChange(value, index, siblingIndex);
              return;
            }
            handleDateChange(value, index);
          }}
          defaultValue={field.value ? dayjs(field.value) : null}
          slotProps={{
            textField: {
              fullWidth: true,
              variant: 'filled',
              sx: {
                height: '50px',
                marginTop: '12px',
                borderRadius: '6px',
                '& .MuiFilledInput-root:after': {
                  border: 'none',
                },
                '& .MuiFilledInput-root:before': {
                  borderBottom: 'none',
                },
                '& .MuiFilledInput-root': {
                  backgroundColor: 'rgba(0, 0, 0, 0.0275)',
                  border: '1px solid transparent',
                },
                '& .MuiFilledInput-root:hover:not(.Mui-disabled,.Mui-error):before':
                  {
                    borderBottom: 'none',
                  },
                '& .MuiFilledInput-root.Mui-focused': {
                  border: '1px solid black',
                },
                '& .MuiFilledInput-root.Mui-error': {
                  border: '1px solid red',
                },
              },
              size: 'small',
              InputProps: {
                disableUnderline: true,
                style: {
                  borderRadius: '6px',
                },
              },
            },
          }}
        />
      </LocalizationProvider>
      {field.errorMessage && (
        <Typography color='error' marginTop={1}>
          {field.errorMessage}
        </Typography>
      )}
    </Box>
  );

  const BigTextField = ({ field, index }: CustomFieldProps) => {
    const extensions = [
      StarterKit,
      Underline,
      enableFormattingShortcuts,
      TextStyleExtended,
      Placeholder.configure({
        placeholder: field.placeholder,
      }),
    ];
    const initialEditor = useEditor({
      extensions: extensions,
      onUpdate: ({ editor }) => {
        if (index !== undefined) {
          handleValueChange(editor.getHTML(), index);
        }
      },
      onSelectionUpdate: ({ editor }) => {
        setMarks({
          bold: editor.isActive('bold'),
          italic: editor.isActive('italic'),
          underline: editor.isActive('underline'),
          bulletList: editor.isActive('bulletList'),
          numberedList: editor.isActive('orderedList'),
          heading: editor.isActive('heading'),
          textStyle: editor.isActive('textStyle'),
        });
      },
      content: field.value,
      autofocus: false,
    });
    if (!initialEditor) {
      return <></>;
    }

    return (
      <Box marginBottom='24px'>
        <FieldHeader title={field.fieldTitle} isMandatory={field.isMandatory} />
        <Box
          onFocus={() => {
            setEditor(initialEditor);
            resetMarks();
          }}
          sx={multiLineInputFieldStyle}
        >
          <EditorContent editor={initialEditor} />
        </Box>
        {field.errorMessage && (
          <Typography color='error' marginTop={1}>
            {field.errorMessage}
          </Typography>
        )}
      </Box>
    );
  };

  const DropdownField = ({
    field,
    index,
    siblingIndex,
    isSibling,
  }: CustomFieldProps) => {
    const [selectedItem, setSelected] = useState(field.value);

    return (
      <Box
        flexGrow={1}
        marginBottom='24px'
        maxWidth='460px'
        onFocus={() => {
          setEditor(null);
          resetMarks();
        }}
      >
        <FormControl fullWidth hiddenLabel>
          <FieldHeader
            title={field.fieldTitle}
            isMandatory={field.isMandatory}
          />
          <Select
            variant='filled'
            disableUnderline
            displayEmpty
            sx={inputFieldStyle}
            onChange={(event) => {
              if (index !== undefined) {
                setSelected(event.target.value);
                if (
                  isSibling &&
                  siblingIndex !== undefined &&
                  siblingIndex >= 0
                ) {
                  handleSiblingValueChange(
                    event.target.value,
                    siblingIndex,
                    index,
                  );
                  return;
                }
                handleValueChange(event.target.value, index);
              }
            }}
            value={selectedItem}
          >
            <MenuItem disabled value={''}>
              Select item
            </MenuItem>
            {field.dropdownItems.map((item, itemIndex) => (
              <MenuItem value={item} key={itemIndex}>
                {item}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {field.errorMessage && (
          <Typography color='error' marginTop={1}>
            {field.errorMessage}
          </Typography>
        )}
      </Box>
    );
  };

  const generateField = (field: DynamicField, index: number) => {
    const fieldInfo = field;
    switch (fieldInfo.type) {
      case fieldTypes.title:
        return <TitleField key={fieldInfo.fieldId} field={fieldInfo} />;
      case fieldTypes.descriptionText:
        return (
          <DescriptionTextField key={fieldInfo.fieldId} field={fieldInfo} />
        );
      case fieldTypes.datePicker:
        return (
          <DateField key={fieldInfo.fieldId} field={fieldInfo} index={index} />
        );
      case fieldTypes.dropdown:
        return (
          <DropdownField
            key={fieldInfo.fieldId}
            field={fieldInfo}
            index={index}
          />
        );
      case fieldTypes.bigTextField:
        return (
          <BigTextField
            key={fieldInfo.fieldId}
            field={fieldInfo}
            index={index}
          />
        );
      case fieldTypes.smallTextField:
        return (
          <Stack
            key={fieldInfo.fieldId}
            direction='row'
            justifyContent='space-between'
            spacing={1}
            width='100%'
            marginBottom='24px'
          >
            <SmallTextField info={fieldInfo} rootIndex={index} />
            {fieldInfo.siblings &&
              fieldInfo.siblings.map((sibling, siblingIndex) => {
                switch (sibling.type) {
                  case 1:
                    return (
                      <DateField
                        key={sibling.fieldId}
                        field={sibling}
                        index={index}
                        siblingIndex={siblingIndex}
                        isSibling
                      />
                    );
                  case 2:
                    return (
                      <DropdownField
                        key={sibling.fieldId}
                        field={sibling}
                        index={index}
                        siblingIndex={siblingIndex}
                        isSibling
                      />
                    );
                  default:
                    return (
                      <SmallTextField
                        key={sibling.fieldId}
                        info={sibling}
                        siblingIndex={siblingIndex}
                        rootIndex={index}
                        isSibling
                      />
                    );
                }
              })}
          </Stack>
        );
      default:
        return <></>;
    }
  };

  return (
    <Box paddingTop='40px' paddingX='56px'>
      {dataFields.map((field, index) => (
        <Box key={index}>{generateField(field, index)}</Box>
      ))}
    </Box>
  );
};

export default CustomFields;
